'use client'

// Mission-briefing typewriter. Types `text` character-by-character with
// a blinking caret, holds for `holdSeconds`, then back-deletes the same
// text and fades the caret out. GSAP drives the timeline so we can
// chain phases cleanly and snap idx to integers (no half-characters).

import { useEffect, useRef } from 'react'
import gsap from 'gsap'

interface Props {
  text: string
  delaySec?:    number    // wait before typing starts
  typeSpeed?:   number    // seconds per character (default 0.045)
  holdSec?:     number    // hold completed text this long before deleting
  deleteSpeed?: number    // seconds per character on delete (default 0.025)
  className?:   string
  style?: React.CSSProperties
  caretColor?:  string
}

export default function TypewriterText({
  text,
  delaySec   = 0,
  typeSpeed  = 0.045,
  holdSec    = 5,
  deleteSpeed = 0.025,
  className,
  style,
  caretColor = '#9FAF08',
}: Props) {
  const textRef  = useRef<HTMLSpanElement>(null)
  const caretRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    const el    = textRef.current
    const caret = caretRef.current
    if (!el || !caret) return

    // If the user prefers reduced motion, just show the text statically.
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced) {
      el.textContent = text
      caret.style.opacity = '0'
      return
    }

    el.textContent = ''
    const obj = { idx: 0 }
    const tl = gsap.timeline({ delay: delaySec })

    // Caret on
    tl.set(caret, { autoAlpha: 1 }, 0)

    // Type
    tl.to(obj, {
      idx: text.length,
      duration: text.length * typeSpeed,
      ease: 'none',
      snap: 'idx',
      onUpdate: () => {
        el.textContent = text.slice(0, Math.floor(obj.idx))
      },
    })

    // Hold for the requested duration
    if (holdSec > 0) tl.to({}, { duration: holdSec })

    // Back-delete (slightly faster than typing — feels like a swipe)
    tl.to(obj, {
      idx: 0,
      duration: text.length * deleteSpeed,
      ease: 'power2.in',
      snap: 'idx',
      onUpdate: () => {
        el.textContent = text.slice(0, Math.floor(obj.idx))
      },
    })

    // Caret fades out after the line is empty
    tl.to(caret, { autoAlpha: 0, duration: 0.3 }, '+=0.05')

    return () => { tl.kill() }
  }, [text, delaySec, typeSpeed, holdSec, deleteSpeed])

  return (
    <span className={className} style={{ ...style, whiteSpace: 'nowrap', display: 'inline-block' }}>
      <style>{`
        @keyframes tw-caret-blink {
          0%, 49.9% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
        .tw-caret {
          display: inline-block;
          margin-left: 2px;
          color: var(--tw-caret-color, #9FAF08);
          animation: tw-caret-blink 0.6s steps(1) infinite;
          font-weight: 400;
        }
      `}</style>
      <span ref={textRef} />
      <span
        ref={caretRef}
        className="tw-caret"
        aria-hidden="true"
        style={{ ['--tw-caret-color' as string]: caretColor, opacity: 0 }}
      >|</span>
    </span>
  )
}
